import React, { useEffect, useState } from "react";

/**
 * 14031 / 14033 — Gap textarea (+ existing citations when present), rendered
 * for non-compliant findings underneath the Risk Finding footer.
 *
 * Citation authoring UI is deferred to a follow-up ticket; this block only
 * renders the Regulatory References section when the snapshot already carries
 * citations to display.
 */
const RegulatoryGapBlock = ({
  scoredRow,
  initialGapText,
  citations = [],
  isAdmin = false,
  isRequired = false,
  isHighlighted = false,
  onSaveGap = null,
}) => {
  const seedText =
    initialGapText ??
    (scoredRow?.sme_review_notes && scoredRow.sme_review_notes.trim()) ??
    (scoredRow?.gap_text && String(scoredRow.gap_text).trim()) ??
    "";
  const [text, setText] = useState(seedText);
  const [savedText, setSavedText] = useState(seedText);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(seedText);
    setSavedText(seedText);
  }, [seedText]);

  const flush = async () => {
    if ((text ?? "") === (savedText ?? "")) return;
    if (typeof onSaveGap !== "function") return;
    setSaving(true);
    try {
      await onSaveGap(text && text.trim() ? text : null);
      setSavedText(text ?? "");
    } finally {
      setSaving(false);
    }
  };

  const emptyText = !text || text.trim().length === 0;
  const showHighlight = isHighlighted || (isRequired && emptyText);

  return (
    <div
      className={`mt-2 rounded-md border bg-white p-3 space-y-3 ${
        showHighlight ? "border-rose-300 bg-rose-50/40" : "border-gray-200"
      }`}
    >
      {citations.length > 0 ? (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Regulatory References
          </div>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {citations.map((c, i) => (
              <li
                key={c.id || `${c.title}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] text-indigo-700"
                title={c.data}
              >
                <span className="font-semibold">{c.title}</span>
                {c.data ? <span className="text-indigo-500">{c.data}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Gap
          </span>
          {saving ? (
            <span className="text-[10px] text-gray-400">Saving…</span>
          ) : null}
        </div>
        <div className="mt-1.5">
          {isAdmin ? (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={flush}
              rows={Math.min(6, Math.max(2, (text || "").split("\n").length + 1))}
              placeholder={
                isRequired
                  ? "Describe the gap and the corrective action expected from the vendor."
                  : "Optional gap commentary."
              }
              className="w-full text-sm text-gray-800 bg-white border border-gray-300 rounded-md p-2.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            />
          ) : text ? (
            <p className="w-full text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-md p-2.5 whitespace-pre-wrap">
              {text}
            </p>
          ) : (
            <p className="w-full text-sm italic text-gray-400 bg-gray-50 border border-gray-200 rounded-md p-2.5">
              No gap commentary yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegulatoryGapBlock;
